import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Ruler, Users, Bed } from "@phosphor-icons/react";
import Reveal from "../components/Reveal";
import PageSeo from "../components/PageSeo";
import { fetchRooms } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SiteSettingsContext";

export default function RoomsPage() {
  const { t, r, lang } = useLang();
  const settings = useSettings();
  const [rooms, setRooms] = useState([]);

  // stable imports/setters — safe to run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRooms().then(setRooms).catch(() => {}); }, []);

  const seoTitle = lang === "en" ? settings.rooms_meta_title_en : settings.rooms_meta_title_it;
  const seoDesc = lang === "en" ? settings.rooms_meta_desc_en : settings.rooms_meta_desc_it;

  return (
    <div className="pt-32 pb-24 lg:pb-40 bg-[#09090b] min-h-screen" data-testid="rooms-page">
      <PageSeo title={seoTitle} description={seoDesc} pathIt="/it/camere" pathEn="/en/rooms" />
      <div className="container-x">
        <Reveal>
          <div className="overline mb-4">{t.rooms.overline}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1] max-w-3xl">
            {t.rooms.title.split("\n").map((l, i) => (<span key={l || `rp-line-${i}`} className="block">{l}</span>))}
          </h1>
          <p className="mt-6 text-white/70 max-w-xl">{t.rooms.subtitle}</p>
        </Reveal>

        <div className="mt-16 space-y-16 lg:space-y-24">
          {rooms.map((room, i) => {
            const name = lang === "it" ? room.name_it : room.name_en;
            const short = lang === "it" ? room.short_it : room.short_en;
            const desc = lang === "it" ? room.description_it : room.description_en;
            const slug = lang === "it" ? room.slug_it : room.slug_en;
            const reverse = i % 2 === 1;
            return (
              <Reveal key={room.id}>
                <div className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div className="lg:col-span-7">
                    <Link to={r.room(slug)} className="card-hover block relative overflow-hidden aspect-[5/4] border border-white/10" data-testid={`room-list-card-${slug}`}>
                      <img src={room.cover} alt={name} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </Link>
                  </div>
                  <div className="lg:col-span-5">
                    <div className="overline mb-3">{t.rooms.from} €{room.price_from} / {t.rooms.perNight}</div>
                    <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-4">{name}</h2>
                    <p className="text-white/70 mb-6">{short}</p>
                    <p className="text-white/55 text-sm leading-relaxed mb-8">{desc}</p>
                    <div className="flex items-center gap-6 text-xs text-white/60 mb-8">
                      <span className="inline-flex items-center gap-1.5"><Ruler size={14} /> {room.size_sqm}m²</span>
                      <span className="inline-flex items-center gap-1.5"><Users size={14} /> {room.occupancy}</span>
                      <span className="inline-flex items-center gap-1.5"><Bed size={14} /> {lang === "it" ? room.bed_type_it : room.bed_type_en}</span>
                    </div>
                    <Link to={r.room(slug)} className="inline-flex items-center gap-3 btn-ghost px-8 py-4 text-xs uppercase tracking-[0.2em]" data-testid={`room-details-${slug}`}>
                      {t.cta.viewDetails} <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  );
}
